import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * <ScrollToTop> — resets scroll to (0, 0) on every meaningful route change.
 *
 * Mounted inside <BrowserRouter> but outside <Routes>. This component is
 * intentionally NOT re-mounted across route changes (that's what makes the
 * `useRef` tracker below reliable — unlike component-level refs inside pages
 * that get unmounted by React Router's `<Routes key={location.pathname}>`).
 *
 * Policy (in priority order):
 *   1. Hash-only change on the same pathname (#anchor jumps) → NO scroll.
 *   2. Cell-to-cell transition inside Dharshana (both prev + current are
 *      `/dharshana/{slug}`) → NO scroll. This is the "one scene, twelve
 *      angles" illusion — the user should stay at their scroll position.
 *   3. Cell-to-scene transition (`/dharshana/{slug}` → `/dharshana` exact)
 *      → NO scroll. Same illusion.
 *   4. Scene-to-cell (`/dharshana` → `/dharshana/{slug}`) → SCROLL to top.
 *      The scene view is ~4000px tall and cell content is ~1200px; if we
 *      don't reset, users land past the cell hero.
 *   5. Everything else (home tile → mini-app, footer action → target,
 *      confirmation panels, etc.) → SCROLL to top.
 *
 * `behavior: "instant"` — smooth scroll feels laggy and gets clamped by
 * framer-motion's AnimatePresence height changes.
 */
const DHARSHANA_CELL = /^\/dharshana\/[^/]+/;

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPathname = useRef(pathname);
  const prevHash = useRef(hash);

  useEffect(() => {
    const wasCell = DHARSHANA_CELL.test(prevPathname.current);
    const isCell = DHARSHANA_CELL.test(pathname);
    const wasScene = prevPathname.current === "/dharshana";
    const isScene = pathname === "/dharshana";

    // Cell → cell OR cell → scene: keep the user's scroll (in-place).
    const dharshanaInPlace = (wasCell && isCell) || (wasCell && isScene);

    // Hash-only change on the same pathname: keep scroll (browser handles it).
    const hashOnlyChange =
      prevPathname.current === pathname && prevHash.current !== hash;

    // Any navigation carrying a hash (initial load OR route change) —
    // the destination page owns the scroll target (e.g. /vision hash-driven
    // scale sub-section jumps). Do not clobber with a scroll-to-top.
    const hasHash = hash && hash.length > 1;

    // Note: scene → cell falls through to the scroll branch by design.
    if (!dharshanaInPlace && !hashOnlyChange && !hasHash) {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      } catch {
        window.scrollTo(0, 0);
      }
    }

    prevPathname.current = pathname;
    prevHash.current = hash;
  }, [pathname, hash]);

  return null;
}
